/**
 * Product Compare System
 * Maneja la funcionalidad de comparar productos usando localStorage + cookies híbrido
 * Mejora: la URL de "ir a comparar" ahora incluye ?ids=...&handles=...
 */

// console.log('Product Compare JS Loading...');

class ProductCompare {
  constructor() {
    // console.log('ProductCompare initialized');
    this.storageKey = 'compare_products';
    this.cookieName = 'compare_products';
    this.maxProducts = 3;
    this.products = this.getStoredProducts();
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateUI();
    // Sincronizar entre pestañas
    this.syncAcrossTabs();
    
    // Si ya hay productos guardados, mostrar la barra flotante
    if (this.products.length > 0) {
      // Esperar un poco para que el DOM esté completamente cargado
      setTimeout(() => {
        this.showFloatingBar();
      }, 500);
    }
  }

  bindEvents() {
    // Botones de agregar al comparador
    document.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-compare-handle]');
      if (addBtn) {
        e.preventDefault();
        const productId = addBtn.dataset.compareAdd;
        const productHandle = addBtn.dataset.compareHandle; // <-- LEER HANDLE AQUÍ
        // console.log('Add product clicked:', { productId, productHandle, buttonElement: addBtn }); // Debug
        this.addProduct(productId, productHandle); // <-- PASAR HANDLE
        return;
      }
      
      // Fallback para botones sin data-compare-handle pero con data-compare-add
      const fallbackBtn = e.target.closest('[data-compare-add]:not([data-compare-handle])');
      if (fallbackBtn) {
        e.preventDefault();
        const productId = fallbackBtn.dataset.compareAdd;
        console.log('Fallback add product clicked:', { productId, buttonElement: fallbackBtn }); // Debug
        this.addProduct(productId, ''); // Sin handle
        return;
      }

      // Botones de remover del comparador
      const removeBtn = e.target.closest('[data-compare-remove]');
      if (removeBtn) {
        e.preventDefault();
        const productId = removeBtn.dataset.compareRemove;
        console.log('Remove product clicked:', productId); // Debug
        this.removeProduct(productId);
      }

      // Botón de limpiar comparador
      const cleanBtn = e.target.closest('[data-compare-clean]');
      if (cleanBtn) {
        e.preventDefault();
        console.log('Clean all clicked'); // Debug
        this.clearAll();
      }

      // Botón de página de comparar (múltiples selectores)
      const pageBtn = e.target.closest('.btn-compare-now, #go-to-compare, .btn-float-compare, #btn-float-compare');
      if (pageBtn) {
        e.preventDefault();
        console.log('Compare page clicked'); // Debug
        this.goToComparePage();
      }

      // Botón de cerrar modal
      const closeBtn = e.target.closest('[data-compare-close], #close-compare-modal');
      if (closeBtn) {
        e.preventDefault();
        console.log('Close modal clicked'); // Debug
        this.closeModal();
      }

      // Botón de abrir modal desde floating bar
      const openBtn = e.target.closest('[data-compare-open], .btn-float-view, #btn-float-view');
      if (openBtn) {
        e.preventDefault();
        console.log('Open modal clicked'); // Debug
        this.openModal();
      }

      // Overlay del modal
      const overlay = e.target.closest('#compare-modal-overlay');
      if (overlay) {
        e.preventDefault();
        console.log('Overlay clicked'); // Debug
        this.closeModal();
      }

      // Click en overlay para cerrar modal
      if (e.target.classList.contains('compare-modal-overlay')) {
        console.log('Overlay clicked'); // Debug
        this.closeModal();
      }
    });

    // Cerrar modal con ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeModal();
      }
    });
  }

  getStoredProducts() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  }

  saveProducts() {
    try {
      // Guardar en localStorage para acceso rápido
      localStorage.setItem(this.storageKey, JSON.stringify(this.products));
      
      // Guardar solo IDs en cookie para server-side access (ahorrar espacio)
      const productIds = this.products.map(p => p.id).join(',');
      this.setCookie(this.cookieName, productIds, 30); // 30 días
    } catch (error) {
      console.error('Error saving products:', error);
    }
  }

  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  }

  syncAcrossTabs() {
    // Escuchar cambios en localStorage desde otras pestañas
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.products = this.getStoredProducts();
        this.updateUI();
      }
    });
  }

  // Ahora recibe también el handle
  addProduct(productId, productHandle) {
    if (!productId && !productHandle) return;

    // Encontrar el botón que se presionó (por handle primero, luego por id)
    const activeButton =
      (productHandle && document.querySelector(`[data-compare-handle="${productHandle}"]`)) ||
      (productId && document.querySelector(`[data-compare-handle][data-compare-add="${productId}"]`));
    if (activeButton) {
      activeButton.classList.add('loading');
    }

    // Verificar si ya está en la lista (por id o por handle)
    if (this.products.find(p => (productId && p.id == productId) || (productHandle && p.handle === productHandle))) {
      this.showNotification('Product already in compare list');
      this.showFloatingBar();
      if (activeButton) activeButton.classList.remove('loading');
      return;
    }

    // Verificar límite máximo
    if (this.products.length >= this.maxProducts) {
      this.showNotification(`Maximum ${this.maxProducts} products allowed`);
      if (activeButton) activeButton.classList.remove('loading');
      return;
    }

    try {
      // Obtener datos del producto desde el DOM (le pasamos también el handle)
      let productData = this.getProductDataFromDOM(productId, productHandle);

      // Asegurar que el handle del botón se guarde en el objeto
      if (productHandle) {
        productData.handle = productHandle;
        if (!productData.url || productData.url === '#') {
          productData.url = `/products/${productHandle}`;
        }
      }
      
      // Debug: mostrar los datos obtenidos
      console.log('Product data obtained:', productData);
      
      if (productData) {
        const isFirstProduct = this.products.length === 0;
        this.products.push(productData);
        this.saveProducts();
        this.updateUI();
        
        // Si es el primer producto, mostrar automáticamente la barra flotante
        if (isFirstProduct) {
          setTimeout(() => {
            this.showFloatingBar();
          }, 200); // Pequeño delay para mejor UX
          this.showNotification('Product added to compare! Check bottom bar.');
        } else {
          this.showNotification('Product added to compare');
        }
      }
    } catch (error) {
      console.error('Error adding product:', error);
      this.showNotification('Error adding product');
    } finally {
      // Remover loading state
      if (activeButton) {
        setTimeout(() => {
          activeButton.classList.remove('loading');
        }, 300);
      }
    }
  }

  /** ---------------------------
   * Helpers para extraer handle
   * --------------------------- */
  sanitizeHandle(maybeHandleOrUrl = '') {
    try {
      // Si es URL absoluta/relativa, intenta extraer /products/{handle}
      const a = document.createElement('a');
      a.href = maybeHandleOrUrl;
      const path = a.pathname || '';
      const m = path.match(/\/products\/([^\/?#]+)/);
      if (m && m[1]) return decodeURIComponent(m[1]);
    } catch (e) {}
    // Si parece un handle “plano”, límpialo
    return (maybeHandleOrUrl || '')
      .trim()
      .replace(/^\/+|\/+$/g, '')
      .split('/').pop();
  }

  extractHandleFromElement(element) {
    if (!element) return '';
    // 1) data attributes comunes
    const raw =
      element.getAttribute('data-product-handle') ||
      element.dataset?.productHandle ||
      element.getAttribute('data-handle') ||
      element.dataset?.handle ||
      '';

    if (raw) return this.sanitizeHandle(raw);

    // 2) canonical / og:url
    const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
    if (canonical) {
      const h = this.sanitizeHandle(canonical);
      if (h) return h;
    }
    const ogUrl = document.querySelector('meta[property="og:url"]')?.getAttribute('content');
    if (ogUrl) {
      const h = this.sanitizeHandle(ogUrl);
      if (h) return h;
    }

    // 3) enlaces a /products/ dentro del elemento
    const link = element.querySelector('a[href*="/products/"]');
    if (link?.getAttribute('href')) {
      const h = this.sanitizeHandle(link.getAttribute('href'));
      if (h) return h;
    }

    // 4) path actual (si es PDP)
    const m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (m && m[1]) return decodeURIComponent(m[1]);

    return '';
  }

  extractHandleFromUrl(url) {
    if (!url) return '';
    const m = url.match(/\/products\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  inferHandleFallback(productData) {
    // intenta por URL del producto
    if (productData?.url) {
      const h = this.extractHandleFromUrl(productData.url);
      if (h) return h;
    }
    return '';
  }

  /** --------------------------- */

  // Ahora acepta también productHandle
  getProductDataFromDOM(productId, productHandle) {
    // Buscar datos del producto en el DOM actual
    let productData = null;

    // 1. Intentar obtener desde window.product (página de producto) - PERO tomar precios del DOM
    if (window.product && window.product.id == productId) {
      // Obtener precios directamente del DOM tal como aparecen en la página
      const mainProductElement = document.querySelector(`[data-product-id="${productId}"]`) || document.querySelector('.main-product');
      let price = '';
      let compareAtPrice = '';
      
      if (mainProductElement) {
        const priceElement = mainProductElement.querySelector('.price-current, .price-sale, .price-regular, .price');
        const comparePriceElement = mainProductElement.querySelector('.price-old, .price-regular s, .price-compare');
        
        if (priceElement) {
          price = priceElement.textContent.trim();
        }
        
        if (comparePriceElement) {
          const tempComparePrice = comparePriceElement.textContent.trim();
          if (tempComparePrice && tempComparePrice !== price) {
            compareAtPrice = tempComparePrice;
          }
        }
      }
      
      // Obtener la imagen principal del producto
      let featuredImage = '/assets/placeholder.svg';
      if (window.product.featured_image) {
        featuredImage = window.product.featured_image;
      } else if (window.product.images && window.product.images.length > 0) {
        featuredImage = window.product.images[0];
      }
      
      productData = {
        id: window.product.id,
        title: window.product.title,
        vendor: window.product.vendor || '',
        handle: productHandle || window.product.handle || this.extractHandleFromElement(mainProductElement) || '',
        price: price || 'N/A',
        compare_at_price: compareAtPrice || null,
        featured_image: featuredImage,
        url: window.product.url || (productHandle || window.product.handle ? `/products/${productHandle || window.product.handle}` : `/products/${this.extractHandleFromElement(mainProductElement)}`)
      };
      
      console.log('Extracted product data from window.product:', productData);
      return productData;
    }

    // 2. Intentar obtener desde metadatos del elemento de producto
    const productElement = document.querySelector(`[data-product-id="${productId}"]`);
    if (productElement) {
      // Buscar script JSON en diferentes ubicaciones posibles
      let jsonScript = productElement.querySelector('script[type="application/json"]');
      
      // Si no se encuentra en el elemento, buscar en elementos padre o hermanos
      if (!jsonScript) {
        const cardElement = productElement.closest('.card, .product-card, .product-item, .grid__item');
        if (cardElement) {
          jsonScript = cardElement.querySelector('script[type="application/json"]') || 
                      cardElement.querySelector('script[data-product-json]');
        }
      }
      
      // Si aún no se encuentra, buscar por ID específicos
      if (!jsonScript) {
        jsonScript = document.querySelector(`#ProductJson-${productId}, #ProductJSON-${productId}`);
      }
      
      if (jsonScript) {
        try {
          const productJson = JSON.parse(jsonScript.textContent);
          
          // También aquí, buscar precios del DOM si es posible
          const elementPrice = productElement.querySelector('.price-current, .price-sale, .price-regular, .price');
          const elementComparePrice = productElement.querySelector('.price-old, .price-regular s, .price-compare');
          
          let price = '';
          let compareAtPrice = '';
          
          if (elementPrice) {
            price = elementPrice.textContent.trim();
          }
          
          if (elementComparePrice) {
            const tempComparePrice = elementComparePrice.textContent.trim();
            if (tempComparePrice && tempComparePrice !== price) {
              compareAtPrice = tempComparePrice;
            }
          }

          const handle = productHandle || productJson.handle || this.extractHandleFromElement(productElement) || '';
          
          // Obtener imagen principal
          let featuredImage = '/assets/placeholder.svg';
          if (productJson.featured_image) {
            featuredImage = productJson.featured_image;
          } else if (productJson.images && productJson.images.length > 0) {
            featuredImage = productJson.images[0];
          }
          
          productData = {
            id: productJson.id,
            title: productJson.title,
            vendor: productJson.vendor || '',
            handle: handle,
            price: price || 'N/A',
            compare_at_price: compareAtPrice || null,
            featured_image: featuredImage,
            url: productJson.url || (handle ? `/products/${handle}` : '#')
          };
          
          console.log('Extracted product data from JSON script:', productData);
          return productData;
        } catch (e) {
          console.error('Error parsing product JSON:', e);
        }
      }
    }

    // 3. Intentar obtener de la página de producto actual
    const mainProduct = document.querySelector(`[data-product-id="${productId}"]`);
    if (mainProduct) {
      productData = this.extractProductDataFromElement(mainProduct, productId);
      if (productHandle && productData && !productData.handle) {
        productData.handle = productHandle;
      }
    }

    // 4. Intentar obtener de cards de productos (AHORA BUSCA POR HANDLE CORRECTAMENTE)
    if (!productData) {
      let productCard = null;
      if (productHandle) {
        productCard = document.querySelector(`[data-compare-handle="${productHandle}"]`);
      }
      if (!productCard && productId) {
        // fallback por id si existe
        productCard = document.querySelector(`[data-compare-handle][data-compare-add="${productId}"]`);
      }
      if (productCard) {
        productData = this.extractProductDataFromCard(productCard, productId);
        if (productHandle && productData) {
          productData.handle = productHandle;
          if (!productData.url || productData.url === '#') {
            productData.url = `/products/${productHandle}`;
          }
        }
      }
    }

    // 5. Datos por defecto si no se encuentra
    if (!productData) {
      productData = {
        id: productId,
        title: 'Product',
        vendor: '',
        handle: productHandle || '',
        price: 0,
        compare_at_price: null,
        featured_image: '/assets/placeholder.svg',
        url: productHandle ? `/products/${productHandle}` : '#'
      };
    }

    return productData;
  }

  extractProductDataFromElement(element, productId) {
    try {
      // Buscar datos en elementos específicos del tema Epic
      const title = element.querySelector('.product-title, h1, .product__title, .main-product-title')?.textContent?.trim() || 'Product';
      const vendor = element.querySelector('.product-brand, .product-vendor, .product__vendor')?.textContent?.trim() || '';
      
      // Epic Theme usa estas clases específicas para precios
      const priceElement = element.querySelector('.price-current, .price-sale, .price-regular');
      const comparePriceElement = element.querySelector('.price-old, .price-regular s, .price-compare');
      
      // Buscar imagen de forma más amplia
      let imageElement = element.querySelector('img, [data-src]');
      
      // Si no encuentra imagen, buscar en la galería del producto
      if (!imageElement) {
        imageElement = element.querySelector('.product-gallery img, .main-product-gallery img, .slider img');
      }
      
      // TOMAR PRECIOS TAL COMO VIENEN - SIN FORMATEAR NADA
      let price = '';
      if (priceElement) {
        price = priceElement.textContent.trim();
      }

      // Precio de comparación tal como viene
      let compareAtPrice = '';
      if (comparePriceElement) {
        const tempComparePrice = comparePriceElement.textContent.trim();
        // Solo guardar si es diferente del precio actual
        if (tempComparePrice && tempComparePrice !== price) {
          compareAtPrice = tempComparePrice;
        }
      }

      // Obtener imagen original
      let featuredImage = '/assets/placeholder.svg';
      if (imageElement) {
        let imageSrc = imageElement.src || imageElement.dataset.src || imageElement.getAttribute('data-src');
        if (imageSrc) {
          if (imageSrc.includes('shopifycdn.com') || imageSrc.includes('cdn.shopify.com')) {
            imageSrc = imageSrc.replace(/_\d+x\d*\.(jpg|jpeg|png|webp)/g, '.$1');
          }
          featuredImage = imageSrc;
        }
      }
      
      // Si aún no tiene imagen, usar window.product si está disponible
      if (featuredImage === '/assets/placeholder.svg' && window.product && window.product.id == productId) {
        if (window.product.featured_image) {
          featuredImage = window.product.featured_image;
        } else if (window.product.images && window.product.images.length > 0) {
          featuredImage = window.product.images[0];
        }
      }

      const handle = this.extractHandleFromElement(element);

      console.log('Epic Theme data extraction (raw):', {
        productId: productId,
        title: title,
        priceElement: priceElement ? priceElement.outerHTML : null,
        rawPrice: price,
        comparePriceElement: comparePriceElement ? comparePriceElement.outerHTML : null,
        rawComparePrice: compareAtPrice,
        handle,
        featuredImage
      });

      return {
        id: productId,
        title: title,
        vendor: vendor,
        handle: handle,
        price: price,
        compare_at_price: compareAtPrice,
        featured_image: featuredImage,
        url: handle ? `/products/${handle}` : `#product-${productId}`
      };
    } catch (error) {
      console.error('Error extracting product data:', error);
      return null;
    }
  }

  extractProductDataFromCard(cardElement, productId) {
    try {
      const card = cardElement.closest('.card, .product-card, .product-item, .grid__item') || cardElement;
      
      // Primero intentar obtener datos del script JSON
      const jsonScript = card.querySelector('script[data-product-json], script[type="application/json"]');
      if (jsonScript) {
        try {
          const productJson = JSON.parse(jsonScript.textContent);
          
          // Usar precios del JSON si están disponibles
          let price = '';
          let compareAtPrice = '';
          
          if (productJson.price !== undefined) {
            price = this.formatMoney(productJson.price);
          }
          
          if (productJson.compare_at_price && productJson.compare_at_price > productJson.price) {
            compareAtPrice = this.formatMoney(productJson.compare_at_price);
          }
          
          // Si no hay precios formateados del JSON, buscar en el DOM
          if (!price) {
            const priceElement = card.querySelector(
              '.price-regular, .price-sale, .price, .product-price .current, .card__price, .money'
            );
            if (priceElement) {
              price = priceElement.textContent.trim();
            }
          }
          
          if (!compareAtPrice) {
            const comparePriceElement = card.querySelector(
              '.price-compare, .price-old, .price__compare, .card__price--compare, .price-regular'
            );
            if (comparePriceElement) {
              const tempComparePrice = comparePriceElement.textContent.trim();
              if (tempComparePrice && tempComparePrice !== price) {
                compareAtPrice = tempComparePrice;
              }
            }
          }
          
          return {
            id: productJson.id,
            title: productJson.title,
            vendor: productJson.vendor || '',
            handle: productJson.handle || '',
            price: price || 'N/A',
            compare_at_price: compareAtPrice || null,
            featured_image: productJson.featured_image || productJson.images?.[0] || '/assets/placeholder.svg',
            url: productJson.url || (productJson.handle ? `/products/${productJson.handle}` : '#')
          };
        } catch (e) {
          console.error('Error parsing card JSON:', e);
        }
      }
      
      // Fallback: extraer del DOM como antes
      const title = card.querySelector('.content-name p, .card-title, .product-title, h3, h4, .card__heading')?.textContent?.trim() || 'Product';
      const vendor = card.querySelector('.brand, .card-vendor, .product-vendor, .card__brand')?.textContent?.trim() || '';
      
      // Buscar precios con selectores específicos de cards
      const priceElement = card.querySelector(
        '.price-regular, .price-sale, .price, .product-price .current, .card__price, .money'
      );
      const comparePriceElement = card.querySelector(
        '.price-compare, .price-old, .price__compare, .card__price--compare, .price-regular'
      );
      const imageElement = card.querySelector('img');
      
      // TOMAR PRECIOS TAL COMO VIENEN - SIN FORMATEAR NADA
      let price = '';
      if (priceElement) {
        price = priceElement.textContent.trim();
      }

      // Precio de comparación tal como viene
      let compareAtPrice = '';
      if (comparePriceElement) {
        const tempComparePrice = comparePriceElement.textContent.trim();
        // Solo guardar si es diferente del precio actual
        if (tempComparePrice && tempComparePrice !== price) {
          compareAtPrice = tempComparePrice;
        }
      }

      // Obtener imagen original (máxima resolución)
      let featuredImage = '/assets/placeholder.svg';
      if (imageElement) {
        let imageSrc = imageElement.src || imageElement.dataset.src || imageElement.getAttribute('data-src');
        if (imageSrc) {
          // Si es una imagen de Shopify, obtener la original sin parámetros de tamaño
          if (imageSrc.includes('shopifycdn.com') || imageSrc.includes('cdn.shopify.com')) {
            // Remover parámetros de tamaño para obtener la imagen original
            imageSrc = imageSrc.replace(/_\d+x\d*\.(jpg|jpeg|png|webp)/g, '.$1');
          }
          featuredImage = imageSrc;
        }
      }
      
      // Si no encontró imagen, buscar en todos los elementos img del card
      if (featuredImage === '/assets/placeholder.svg') {
        const allImages = card.querySelectorAll('img');
        for (const img of allImages) {
          let src = img.src || img.dataset.src || img.getAttribute('data-src');
          if (src && !src.includes('placeholder') && !src.includes('loading')) {
            if (src.includes('shopifycdn.com') || src.includes('cdn.shopify.com')) {
              src = src.replace(/_\d+x\d*\.(jpg|jpeg|png|webp)/g, '.$1');
            }
            featuredImage = src;
            break;
          }
        }
      }

      // Intentar extraer handle desde el card (data attrs o enlaces)
      const handle = this.extractHandleFromElement(card);

      // Intentar URL del card
      let url = '#';
      const link = card.querySelector('a[href*="/products/"]');
      if (link?.getAttribute('href')) {
        url = link.getAttribute('href');
      } else if (handle) {
        url = `/products/${handle}`;
      }

      return {
        id: productId,
        title: title,
        vendor: vendor,
        handle: handle,
        price: price,
        compare_at_price: compareAtPrice,
        featured_image: featuredImage,
        url: url
      };
    } catch (error) {
      console.error('Error extracting card data:', error);
      return null;
    }
  }

  removeProduct(productId) {
    // Encontrar el elemento del producto para animación
    const productElement = document.querySelector(`[data-compare-remove="${productId}"]`)?.closest('.compare-product-item');
    
    if (productElement) {
      // Agregar clase de animación
      productElement.classList.add('removing');
      
      // Esperar que termine la animación antes de remover
      setTimeout(() => {
        this.products = this.products.filter(p => p.id != productId);
        this.saveProducts();
        this.updateUI();
        this.showNotification('Product removed from compare');
      }, 300);
    } else {
      // Si no hay elemento visual, remover inmediatamente
      this.products = this.products.filter(p => p.id != productId);
      this.saveProducts();
      this.updateUI();
      this.showNotification('Product removed from compare');
    }
  }

  clearAll() {
    this.products = [];
    this.saveProducts();
    this.updateUI();
    this.closeModal();
    this.hideFloatingBar();
    this.showNotification('Compare list cleared');
  }

  updateUI() {
    this.updateModal();
    this.updateFloatingBar();
    this.updateCompareButtons();
    this.updateProgress();
    
    // Lógica para mostrar/ocultar elementos
    if (this.products.length === 0) {
      this.hideFloatingBar();
    } else {
      // Si hay productos, mantener la barra flotante visible si ya estaba visible
      const floatingBar = document.querySelector('.product-compare-float');
      if (floatingBar && floatingBar.classList.contains('show')) {
        // Ya está visible, no hacer nada
      }
    }
  }

  updateModal() {
    const modal = document.querySelector('#product-compare-modal');
    const emptyState = modal?.querySelector('#compare-empty-state');
    const productsList = modal?.querySelector('#compare-products-list');
    const footer = modal?.querySelector('#compare-modal-footer');
    
    if (!modal) return;

    if (this.products.length === 0) {
      if (emptyState) emptyState.style.display = 'block';
      if (productsList) productsList.style.display = 'none';
      if (footer) footer.style.display = 'none';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (footer) footer.style.display = 'block';
    if (productsList) {
      productsList.style.display = 'block';
      
      const productsHTML = this.products.map((product, index) => `
        <div class="compare-product-item" style="--index: ${index}">
          <button class="product-remove-simple" data-compare-remove="${product.id}" aria-label="Remove ${product.title}">
            <i class="fa-solid fa-xmark"></i>
          </button>
          <div class="product-main-content">
            <div class="product-image-wrapper">
              <img src="${product.featured_image}" alt="${product.title}" class="compare-product-image" loading="lazy">
            </div>
            <div class="product-info">
              <h4 class="product-title">${product.title}</h4>
              ${product.vendor ? `<p class="product-vendor">${product.vendor}</p>` : ''}
              <div class="product-price">
                <span class="current-price">${product.price}</span>
                ${product.compare_at_price && product.compare_at_price !== product.price ? `<span class="compare-price">${product.compare_at_price}</span>` : ''}
              </div>
            </div>
          </div>
        </div>
      `).join('');

      productsList.innerHTML = productsHTML;
    }
  }

  updateFloatingBar() {
    const floatingBar = document.querySelector('#product-compare-float');
    const productsContainer = document.querySelector('#float-products');
    const countElement = document.querySelector('#product-compare-float .count');
    const productTextElement = document.querySelector('#product-compare-float .product-text');
    
    if (!floatingBar) return;

    // Solo mostrar si hay productos (mayor a 0)
    if (this.products.length === 0) {
      floatingBar.classList.remove('show');
      return;
    }

    // Actualizar contador
    if (countElement) {
      countElement.textContent = this.products.length;
    }

    // Actualizar texto singular/plural
    if (productTextElement) {
      productTextElement.textContent = this.products.length === 1 ? 'Selected product' : 'Selected products';
    }

    // Actualizar imágenes de productos solamente
    if (productsContainer) {
      const imagesHTML = this.products.map(product => `
        <img src="${product.featured_image}" alt="${product.title}" class="float-product-image" loading="lazy">
      `).join('');
      
      productsContainer.innerHTML = imagesHTML;
    }

    // Mostrar la barra si no está visible y hay productos
    if (!floatingBar.classList.contains('show') && this.products.length > 0) {
      setTimeout(() => {
        floatingBar.classList.add('show');
      }, 100);
    }
  }

  updateProgress() {
    // Actualizar barra de progreso en el modal
    const progressFill = document.querySelector('#compare-progress-fill');
    const progressText = document.querySelector('#compare-progress-text');
    
    if (progressFill) {
      const percentage = (this.products.length / this.maxProducts) * 100;
      progressFill.style.width = `${percentage}%`;
    }
    
    if (progressText) {
      const text = this.products.length === 0 ? '0 of 3 products' :
                   this.products.length === 1 ? '1 of 3 products' :
                   `${this.products.length} of ${this.maxProducts} products`;
      progressText.textContent = text;
    }
  }

  showFloatingBar() {
    const floatingBar = document.querySelector('#product-compare-float');
    // Solo mostrar si hay productos (mayor a 0)
    if (floatingBar && this.products.length > 0) {
      floatingBar.classList.add('show');
      
      // Añadir una pequeña animación de entrada
      setTimeout(() => {
        floatingBar.style.transform = 'translateX(-50%) translateY(0)';
      }, 100);
    }
  }

  hideFloatingBar() {
    const floatingBar = document.querySelector('#product-compare-float');
    if (floatingBar) {
      floatingBar.classList.remove('show');
    }
  }

  updateCompareButtons() {
    // Buscar todos los botones de compare (con y sin handle)
    const buttonsWithHandle = document.querySelectorAll('[data-compare-handle]');
    const buttonsWithoutHandle = document.querySelectorAll('[data-compare-add]:not([data-compare-handle])');
    
    // Actualizar botones con handle
    buttonsWithHandle.forEach(button => {
      const productId = button.dataset.compareAdd;
      const productHandle = button.dataset.compareHandle;
      const isInCompare = this.products.find(p => (productId && p.id == productId) || (productHandle && p.handle === productHandle));
      
      if (isInCompare) {
        button.classList.add('in-compare');
        button.setAttribute('title', 'Already in comparison');
      } else {
        button.classList.remove('in-compare');
        button.setAttribute('title', 'Add to comparison');
      }
    });
    
    // Actualizar botones sin handle (fallback)
    buttonsWithoutHandle.forEach(button => {
      const productId = button.dataset.compareAdd;
      const isInCompare = this.products.find(p => productId && p.id == productId);
      
      if (isInCompare) {
        button.classList.add('in-compare');
        button.setAttribute('title', 'Already in comparison');
      } else {
        button.classList.remove('in-compare');
        button.setAttribute('title', 'Add to comparison');
      }
    });
    
    // console.log('Updated compare buttons:', {
    //   withHandle: buttonsWithHandle.length,
    //   withoutHandle: buttonsWithoutHandle.length,
    //   productsInCompare: this.products.length
    // });
  }

  openModal() {
    const modal = document.querySelector('#product-compare-modal');
    const overlay = document.querySelector('#compare-modal-overlay');
    
    if (modal && overlay) {
      modal.classList.add('open');
      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      
      // Actualizar el contenido del modal antes de mostrarlo
      this.updateModal();
      this.updateProgress();
    }
  }

  closeModal() {
    const modal = document.querySelector('#product-compare-modal');
    const overlay = document.querySelector('#compare-modal-overlay');
    
    if (modal && overlay) {
      modal.classList.remove('open');
      overlay.classList.remove('open');
      document.body.style.overflow = '';
    }
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') {
      cents = parseInt(cents) || 0;
    }
    
    // Usar exactamente la misma lógica que minicart.js
    const { moneyFormat = '', locale = 'en', currency = 'EUR' } = window.CartConfig || {};
    
    // Si existe window.theme?.currency?.formatMoney
    if (window.theme?.currency?.formatMoney) {
      return window.theme.currency.formatMoney(cents, moneyFormat);
    }
    
    // Usar Intl.NumberFormat como fallback (igual que minicart)
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  }

  showNotification(message) {
    // Crear notificación simple
    const notification = document.createElement('div');
    notification.className = 'compare-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #333;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      transition: opacity 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }

  // Navegar a la página de comparación
  goToComparePage() {
    if (this.products.length === 0) {
      this.showNotification('Add some products to compare first');
      return;
    }

    this.closeModal();

    // Construir URL con ids y handles
    const idsParam = this.products.map(p => encodeURIComponent(p.id)).join(',');
    const handlesParam = this.products.map(p => encodeURIComponent(p.handle || '')).join(',');

    const url = new URL('/pages/compare', window.location.origin);
    url.searchParams.set('ids', idsParam);
    url.searchParams.set('handles', handlesParam);

    window.location.href = url.toString();
  }


  // Renderizar página de comparación (llamado desde page.compare.liquid)
  renderComparePage() {
    // En lugar de renderizar desde JavaScript, solo verificamos si hay productos
    // La página ya está renderizada por Liquid, solo necesitamos manejar el estado vacío
    // si se eliminan todos los productos dinámicamente
    const contentDiv = document.getElementById('comparePageContent');
    if (!contentDiv) return;

    // Solo actuar si realmente no hay productos y la página muestra contenido
    if (this.products.length === 0) {
      const existingTable = contentDiv.querySelector('.compare-details-section');
      if (existingTable) {
        // Solo reemplazar si hay tabla visible pero no productos en localStorage
        contentDiv.innerHTML = `
          <div class="compare-empty-state">
            <div class="empty-state-content">
              <h2>No products to compare</h2>
              <p>Add some products to start comparing them.</p>
              <a href="/collections/all" class="button">Browse Products</a>
            </div>
          </div>
        `;
      }
    }
  }

    // Navegar a la página de comparación
  goToComparePage() {
    if (this.products.length === 0) {
      this.showNotification('Add some products to compare first');
      return;
    }

    this.closeModal();

    // Construir URL con ids y handles
    const idsParam = this.products.map(p => encodeURIComponent(p.id)).join(',');
    const handlesParam = this.products.map(p => encodeURIComponent(p.handle || '')).join(',');

    const url = new URL('/pages/compare', window.location.origin);
    url.searchParams.set('ids', idsParam);
    url.searchParams.set('handles', handlesParam);

    window.location.href = url.toString();
  }
}

// Inicializar cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
  // console.log('DOM Content Loaded - Initializing Product Compare');
  window.productCompare = new ProductCompare();
  // console.log('Product Compare Ready');
});

// Debug: verificar botones de compare cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    const compareButtons = document.querySelectorAll('[data-compare-handle], [data-compare-add]');
    // console.log('🔍 Compare buttons found:', compareButtons.length);
    
    compareButtons.forEach((btn, index) => {
      // console.log(`Button ${index + 1}:`, {
      //   element: btn,
      //   productId: btn.dataset.compareAdd,
      //   productHandle: btn.dataset.compareHandle,
      //   classes: btn.className
      // });
    });
    
    // Verificar si window.product está disponible
    if (window.product) {
      // console.log('✅ window.product available:', {
      //   id: window.product.id,
      //   title: window.product.title,
      //   handle: window.product.handle,
      //   featured_image: window.product.featured_image
      // });
    } else {
      // console.log('❌ window.product not available');
    }
  }, 1000);
});
